// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type ComponentType, type KeyboardEvent } from 'react';
import findIndex from 'lodash/findIndex';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { AnalyticsEventToProps, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { transformModuleKey } from '@atlassian/jira-servicedesk-automation-common/src/analytics';
import type { BlueprintModuleKey } from '../../../../model';
import messages from './messages';
import { Container, TitleComponent } from './styled';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ListLabel = (props: any) => (
	<Heading level="h100" as="h2">
		<TitleComponent {...props} />
	</Heading>
);

export type Props = {
	blueprintModuleKeys: BlueprintModuleKey[];
	defaultBlueprintModuleKey: string;
	selectedBlueprint: BlueprintModuleKey;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	BlueprintListComponent: ComponentType<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	BlueprintOptionComponent: ComponentType<any>;
	onSelect: (selectedBlueprintModuleKey: BlueprintModuleKey) => void;
	onCreate: () => void;
	intl: IntlShape;
};

const ContainerWithAnalytics = AnalyticsEventToProps('keyboardShortcut', {
	onKeyDown: 'pressed',
})(Container);

// eslint-disable-next-line jira/react/no-class-components
class SelectBlueprint extends PureComponent<Props> {
	constructor(props: Props) {
		super(props);
		const { defaultBlueprintModuleKey, blueprintModuleKeys } = props;
		this.allBlueprintModuleKeys = [defaultBlueprintModuleKey, ...blueprintModuleKeys];
	}

	onKeyDown = (e: KeyboardEvent<HTMLDivElement>, analyticsEvent: UIAnalyticsEvent): void => {
		switch (e.key) {
			case 'Enter':
				this.props.onCreate();
				fireUIAnalytics(analyticsEvent, 'continue', {
					blueprintKey: transformModuleKey(this.props.selectedBlueprint),
				});
				break;
			case 'ArrowUp':
				e.preventDefault();
				this.selectByIndex(this.getPrevIndex());
				break;
			case 'ArrowDown':
				e.preventDefault();
				this.selectByIndex(this.getNextIndex());
				break;
			default:
				break;
		}
	};

	getNextIndex() {
		const selectedIndex = this.getSelectedIndex();
		if (selectedIndex < this.allBlueprintModuleKeys.length - 1) {
			return selectedIndex + 1;
		}
		return selectedIndex;
	}

	getPrevIndex() {
		const selectedIndex = this.getSelectedIndex();
		if (selectedIndex > 0) {
			return selectedIndex - 1;
		}
		return selectedIndex;
	}

	getSelectedIndex() {
		const { selectedBlueprint } = this.props;
		return findIndex(
			this.allBlueprintModuleKeys,
			(blueprintModuleKey) => blueprintModuleKey === selectedBlueprint,
		);
	}

	selectByIndex(newSelectedIndex: number): void {
		const selectedIndex = this.getSelectedIndex();
		if (selectedIndex !== newSelectedIndex) {
			this.props.onSelect(this.allBlueprintModuleKeys[newSelectedIndex]);
		}
	}

	allBlueprintModuleKeys: BlueprintModuleKey[];

	render() {
		const {
			BlueprintListComponent,
			BlueprintOptionComponent,
			defaultBlueprintModuleKey,
			intl: { formatMessage },
		} = this.props;
		return (
			<ContainerWithAnalytics tabIndex={0} onKeyDown={this.onKeyDown}>
				<BlueprintOptionComponent blueprintModuleKey={defaultBlueprintModuleKey} />
				<ListLabel>{formatMessage(messages.selectBlueprint)}</ListLabel>
				<BlueprintListComponent />
			</ContainerWithAnalytics>
		);
	}
}

export default injectIntl(SelectBlueprint);
